import { useEffect, useState } from 'react'
import { trackEvent } from '@/lib/analytics'
import {
  activeDurationSeconds,
  claimPropertyImpression,
  galleryActionEvent,
  propertyEventParams,
  shouldLogPropertySaved,
  shouldLogPropertyView,
} from '@/lib/analytics/propertyEventParams'

const impressions = new Map<string, Set<string>>()
let lastView: { id: number; at: number } | null = null

export function trackPropertyImpression(input: Parameters<typeof propertyEventParams>[0] & {
  resultSetKey: string
}): void {
  const propertyId = input.propertyId
  if (propertyId == null) return
  const seen = impressions.get(input.resultSetKey) ?? new Set<string>()
  impressions.set(input.resultSetKey, seen)
  if (!claimPropertyImpression(seen, propertyId)) return
  trackEvent('property_impression', propertyEventParams(input))
}

export function trackPropertyClick(input: Parameters<typeof propertyEventParams>[0]): void {
  trackEvent('property_click', propertyEventParams(input))
}

export function trackPropertyView(input: Parameters<typeof propertyEventParams>[0], nowMs = Date.now()): boolean {
  const propertyId = input.propertyId
  if (propertyId == null) return false
  if (
    !shouldLogPropertyView({
      previousId: lastView?.id,
      previousAtMs: lastView?.at,
      propertyId,
      nowMs,
    })
  ) {
    return false
  }
  lastView = { id: propertyId, at: nowMs }
  trackEvent('property_view', propertyEventParams(input))
  return true
}

export function trackPropertySaved(
  input: Parameters<typeof propertyEventParams>[0] & { requestSucceeded: boolean; likedAfter: boolean },
): void {
  if (!shouldLogPropertySaved(input)) return
  trackEvent('property_saved', propertyEventParams(input))
}

export function trackGalleryOpen(propertyId: number): void {
  if (galleryActionEvent('open') == null) return
  trackEvent('gallery_open', propertyEventParams({ propertyId, source: 'property_gallery' }))
}

export function trackTimeOnProperty(propertyId: number, elapsedMs: number, extra?: Parameters<typeof propertyEventParams>[0]): void {
  const durationSeconds = activeDurationSeconds(elapsedMs)
  if (durationSeconds == null) return
  trackEvent(
    'time_on_property',
    propertyEventParams({ ...extra, propertyId, durationSeconds, source: extra?.source ?? 'property_detail' }),
  )
}

export function usePropertyImpression(
  input: Parameters<typeof propertyEventParams>[0] & { resultSetKey: string },
) {
  const [node, setNode] = useState<Element | null>(null)
  const propertyId = input.propertyId
  const resultSetKey = input.resultSetKey
  const listingPosition = input.listingPosition
  const cityId = input.cityId
  const areaId = input.areaId
  const categoryId = input.categoryId
  const intent = input.intent
  const source = input.source

  useEffect(() => {
    if (!node || propertyId == null || !resultSetKey || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return
        trackPropertyImpression({
          propertyId,
          resultSetKey,
          listingPosition,
          cityId,
          areaId,
          categoryId,
          intent,
          source,
        })
        observer.disconnect()
      },
      { threshold: 0.5 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [node, propertyId, resultSetKey, listingPosition, cityId, areaId, categoryId, intent, source])

  return setNode
}

export function usePropertyEngagement(input: Parameters<typeof propertyEventParams>[0]) {
  const propertyId = input.propertyId
  const cityId = input.cityId
  const areaId = input.areaId
  const categoryId = input.categoryId
  const intent = input.intent

  useEffect(() => {
    if (propertyId == null) return
    trackPropertyView({
      propertyId,
      cityId,
      areaId,
      categoryId,
      intent,
      source: 'property_detail',
    })

    let activeStart = document.visibilityState === 'visible' ? Date.now() : null
    let accumulated = 0
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (activeStart != null) {
          accumulated += Date.now() - activeStart
          activeStart = null
        }
        return
      }
      if (activeStart == null) activeStart = Date.now()
    }
    let flushed = false
    const flush = () => {
      if (flushed) return
      flushed = true
      if (activeStart != null) {
        accumulated += Date.now() - activeStart
        activeStart = null
      }
      trackTimeOnProperty(propertyId, accumulated, {
        propertyId,
        cityId,
        areaId,
        categoryId,
        intent,
        source: 'property_detail',
      })
    }
    // Listing cards open detail in a new tab, so closing that tab must still send the duration.
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', flush)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [propertyId, cityId, areaId, categoryId, intent])
}
